import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { Link } from 'react-router-dom';

const GET_APP_INVITE_SETTINGS = gql`
  query GetAppInviteSettings {
    getAppInviteSettings {
      id
      auto_send_enabled
      days_before_checkin
      test_mode_enabled
      test_email
      test_phone
      apple_store_url
    }
  }
`;

const UPDATE_APP_INVITE_SETTINGS = gql`
  mutation UpdateAppInviteSettings($input: UpdateAppInviteSettingsInput!) {
    updateAppInviteSettings(input: $input) {
      id
      auto_send_enabled
      days_before_checkin
      test_mode_enabled
      test_email
      test_phone
      apple_store_url
    }
  }
`;

export default function AppInviteSettings() {
  const [formData, setFormData] = useState({
    auto_send_enabled: false,
    days_before_checkin: 14,
    test_mode_enabled: false,
    test_email: '',
    test_phone: '',
    apple_store_url: 'https://apps.apple.com/app/sweetgrass-properties/id123456789'
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data, loading, error } = useQuery(GET_APP_INVITE_SETTINGS, {
    fetchPolicy: 'cache-and-network'
  });

  const [updateSettings, { loading: saving }] = useMutation(UPDATE_APP_INVITE_SETTINGS, {
    onCompleted: () => {
      setHasChanges(false);
      setSaveSuccess(true);
      const timeoutId = setTimeout(() => setSaveSuccess(false), 3000);
      
      // Store timeout ID for cleanup
      return () => clearTimeout(timeoutId);
    },
    onError: (error) => {
      console.error('Error updating settings:', error);
    }
  });

  useEffect(() => {
    if (data?.getAppInviteSettings) {
      const settings = data.getAppInviteSettings;
      setFormData({
        auto_send_enabled: settings.auto_send_enabled,
        days_before_checkin: settings.days_before_checkin,
        test_mode_enabled: settings.test_mode_enabled,
        test_email: settings.test_email || '',
        test_phone: settings.test_phone || '',
        apple_store_url: settings.apple_store_url || 'https://apps.apple.com/app/sweetgrass-properties/id123456789'
      });
    }
  }, [data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      await updateSettings({
        variables: {
          input: formData
        }
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading settings</h3>
            <p className="text-sm text-red-700 mt-1">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">App Invite Settings</h1>
          <p className="text-gray-600 mt-1">
            Configure how and when guest app invites are sent
          </p>
        </div>
        <Link
          to="/dashboard/invites"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Invites
        </Link>
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">Settings saved successfully!</p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6 space-y-6">
          
          {/* Auto-Send Settings */}
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Automatic Sending
            </h3>
            
            <div className="space-y-4">
              {/* Auto-send toggle */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700">
                    Enable automatic sending
                  </label>
                  <p className="text-sm text-gray-500">
                    Automatically send app invites to guests before their check-in date
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange('auto_send_enabled', !formData.auto_send_enabled)}
                  className={`${
                    formData.auto_send_enabled ? 'bg-sky-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      formData.auto_send_enabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              {/* Days before check-in */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send invites this many days before check-in
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={formData.days_before_checkin}
                    onChange={(e) => handleInputChange('days_before_checkin', parseInt(e.target.value))}
                    className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                  />
                  <span className="text-sm text-gray-500">days</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Recommended: 14 days to give guests time to download and explore the app
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Test Mode
            </h3>
            
            <div className="space-y-4">
              {/* Test mode toggle */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700">
                    Enable test mode
                  </label>
                  <p className="text-sm text-gray-500">
                    When enabled, all invites will be logged instead of sent, or sent to test contacts
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange('test_mode_enabled', !formData.test_mode_enabled)}
                  className={`${
                    formData.test_mode_enabled ? 'bg-yellow-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      formData.test_mode_enabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              {/* Test contacts */}
              {formData.test_mode_enabled && (
                <div className="space-y-4 pl-4 border-l-2 border-yellow-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test email address
                    </label>
                    <input
                      type="email"
                      value={formData.test_email}
                      onChange={(e) => handleInputChange('test_email', e.target.value)}
                      placeholder="test@example.com"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      If provided, all email invites will be sent to this address instead of the guest
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test phone number
                    </label>
                    <input
                      type="tel"
                      value={formData.test_phone}
                      onChange={(e) => handleInputChange('test_phone', e.target.value)}
                      placeholder="+1234567890"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      If provided, all SMS invites will be sent to this number instead of the guest
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              App Store Link
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apple App Store URL
              </label>
              <input
                type="url"
                value={formData.apple_store_url}
                onChange={(e) => handleInputChange('apple_store_url', e.target.value)}
                placeholder="https://apps.apple.com/app/sweetgrass-properties/id123456789"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
              />
              <p className="text-sm text-gray-500 mt-1">
                This link will be included in all app invite messages
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 rounded-b-lg">
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 ${
              !hasChanges || saving
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-sky-600 hover:bg-sky-700'
            }`}
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}